import clsx from 'clsx';
import { FormSubmitHandler } from 'components/Form';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@clover/web-lib-consumer-ui';
import { NumberPicker } from 'components/NumberPicker';
import { OptionalTextArea } from 'components/OptionalTextArea';
import { Spinner } from 'components/Spinner';
import { ModalForm } from 'components/ModalForm';

import { MENU_ITEM_MODAL_TEST_ID } from 'routes/MenuItemModal/MenuItemModalTestIds';
import { Item, Modifier } from 'olo-service/models';
import { useMerchant } from 'hooks/context/merchant';

import styles from './MenuItemModal.module.scss';
import { ModifiersSelector } from './ModifiersSelector/ModifiersSelector';
import { NewCartItem, CartItem } from 'hooks/context/cart';
import { useMenuItemModalState } from './useMenuItemModalState';
import { MerchantTitle } from 'components/MerchantTitle';
import { useWindowSize } from 'hooks/breakpoints';
import { ImageSize, MenuItemImage } from 'components/MenuItemImage';
import { useMerchantState } from 'hooks/context/MerchantState';

const MAXIMUM_QUANTITY = 100;
const MAXIMUM_CHARACTER_LENGTH = 255;

export type ModifierGroupSelection = Record<Uuid, Modifier[]>;

export type MenuItemModalProps<I extends NewCartItem | CartItem> = {
  cartItem?: I;
  menuItem: Item;
  onClose: () => void;
  onSubmit: (cartItem: I) => void;
};

export const MenuItemModal = function MenuItemModal<I extends NewCartItem | CartItem>({
  cartItem, menuItem, onSubmit, onClose
}: MenuItemModalProps<I>) {
  const { t } = useTranslation();
  const { merchant, isLoading } = useMerchant();
  const { isDineReadOnly } = useMerchantState();
  const { setQuantity, setModifiers, setNote, isAcceptingOrders, cost, ...state } = useMenuItemModalState(menuItem, cartItem);
  const { isDesktop } = useWindowSize();

  const onNoteChange: React.ChangeEventHandler<HTMLTextAreaElement> = useCallback(e => {
    setNote(e.target.value);
  }, [setNote]);

  const item = state as I;
  const handleSubmit: FormSubmitHandler = useCallback((_, isValid) => {
    if (!isAcceptingOrders || !isValid) return;

    onSubmit(item);
    onClose();
  }, [isAcceptingOrders, onSubmit, item, onClose]);
  const hasImage = !!menuItem.images?.length;

  const itemImage = <MenuItemImage item={menuItem} imageSize={isDesktop ? ImageSize.LARGE : ImageSize.MEDIUM} />;

  const isAvailable = menuItem.available;

  const displayBannerMsg = useMemo(() => {
    if (!isLoading && !isAcceptingOrders) return t(`bannerMessage.${isDineReadOnly ? 'dineInClosed': 'paused'}`, { merchantName: merchant?.name });

    if (!isAvailable) return (t('bannerMessage.itemUnavailable'));

  }, [isAcceptingOrders, isAvailable, isDineReadOnly, isLoading, merchant?.name, t]);

  return (
    <ModalForm
      isOpen={true}
      testId={MENU_ITEM_MODAL_TEST_ID.root}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={menuItem.name}
      image={hasImage ? itemImage : undefined}
    >
      <MerchantTitle i18nKey={cartItem ? 'helmet.titleEditItem' : 'helmet.titleItem'} />
      {menuItem.description && <p className={styles.menuItemDescription}>{menuItem.description}</p>}
      <ModifiersSelector
        disabled={!isAcceptingOrders}
        value={state.modifiers}
        groups={menuItem.modifierGroups}
        onChange={setModifiers}
      />
      {(!!state.note || isAcceptingOrders) && (
        <OptionalTextArea
          placeholder={t('modalSpecialInstructionsPlaceholder')}
          value={state.note}
          className={styles.specialInstructions}
          onChange={onNoteChange}
          disabled={!isAcceptingOrders}
          maxLength={MAXIMUM_CHARACTER_LENGTH}
        >
          {t('contactForm.specialInstructions')}
        </OptionalTextArea>
      )}
      <footer className={clsx(styles.bottomTray, { [styles.acceptingOrders]: isAcceptingOrders, [styles.available]: isAvailable })} data-testid={MENU_ITEM_MODAL_TEST_ID.closedBanner}>
        {isLoading && <Spinner />}
        {!isLoading && isAcceptingOrders && isAvailable &&(
          <>
            <NumberPicker
              className={styles.quantityButtons}
              value={state.quantity}
              onChange={setQuantity}
              min={1}
              max={MAXIMUM_QUANTITY}
            />
            <Button
              type="submit"
              className={clsx('h3', { [styles.rounded] : !hasImage, [styles.notRounded] : hasImage || !isDesktop }, styles.addToOrder)}
              data-testid={MENU_ITEM_MODAL_TEST_ID.submitButton}
            >
              <span>{state.cartId ? t('updateOrder') : t('addToOrder')}</span>
              <span data-testid={MENU_ITEM_MODAL_TEST_ID.itemSubtotal}>{t('money', { amount: cost })}</span>
            </Button>
          </>
        )}
        <div data-testid={MENU_ITEM_MODAL_TEST_ID.bannerMessage}>
          {displayBannerMsg}
        </div>
      </footer>
    </ModalForm>
  );
};
